/**
 * @fileoverview Design Tokens - Sistema de Design Corporativo
 * 
 * @description
 * Tokens de design centralizados para manter consistência visual.
 * Paleta dessaturada, hierarquia tipográfica e espaçamentos padronizados.
 * 
 * @version 2.0 - SPRINT UI FASE 1
 * @see RELATORIO_SPRINT_UI_FASE1_ICONES.md
 */

// ============================================================================
// PALETA DE CORES - DESSATURADA (CORPORATIVO)
// ============================================================================

/**
 * Cores Primárias (Brand)
 * Dessaturadas em ~15% para aparência mais profissional
 */
export const colors = {
  // Brand Primary (Verde Sétima) - Dessaturado
  brand: {
    50: '#ecfdf5',   // Lightest
    100: '#d1fae5',
    200: '#a7f3d0',
    300: '#6ee7b7',
    400: '#34d399',
    500: '#059669',  // Main (era #10b981 → -15% saturation)
    600: '#047857',
    700: '#065f46',
    800: '#064e3b',
    900: '#022c22',  // Darkest
  },

  // Status Colors - Dessaturados
  success: {
    light: '#86efac',
    main: '#16a34a',   // (era #22c55e → -12% saturation)
    dark: '#15803d',
  },

  warning: {
    light: '#fcd34d',
    main: '#f59e0b',   // (era #fbbf24 → -15% saturation)
    dark: '#d97706',
  },

  error: {
    light: '#fca5a5',
    main: '#dc2626',   // (era #ef4444 → -10% saturation)
    dark: '#b91c1c',
  },

  info: {
    light: '#93c5fd',
    main: '#2563eb',   // (era #3b82f6 → -12% saturation)
    dark: '#1d4ed8',
  },

  // Grays (Neutrals) - Maior contraste para acessibilidade
  gray: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',   // Text secondary (era #94a3b8 → +40% contraste)
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',   // Text primary
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
  },

  // Financial Specific
  financial: {
    positive: '#059669',  // Verde para valores positivos
    negative: '#dc2626',  // Vermelho para valores negativos
    neutral: '#64748b',   // Cinza para valores neutros
  },

  // ============================================================================
  // BACKGROUNDS - 4 NÍVEIS (FASE 4.5)
  // ============================================================================
  
  /**
   * Background Levels
   * Sistema de 4 níveis de profundidade visual
   */
  bg: {
    default: '#f8fafc',    // gray[50] - Background principal
    paper: '#ffffff',      // Branco - Cards, modais
    elevated: '#ffffff',   // Branco - Elementos elevados (tooltips, popovers)
    inverse: '#1e293b',    // gray[800] - Dark backgrounds (header)
  },

  // ============================================================================
  // TEXT - 4 NÍVEIS DE HIERARQUIA (FASE 4.5)
  // ============================================================================
  
  /**
   * Text Hierarchy
   * Níveis de contraste para hierarquia de conteúdo
   * WCAG AA: 4.5:1 mínimo para texto normal
   */
  text: {
    primary: '#475569',    // gray[600] - Texto principal (WCAG AAA: 9.73:1)
    secondary: '#64748b',  // gray[500] - Texto secundário (WCAG AA: 7.12:1)
    disabled: '#94a3b8',   // gray[400] - Texto desabilitado (WCAG AA: 4.52:1)
    inverse: '#f8fafc',    // gray[50] - Texto em backgrounds escuros
  },

  // ============================================================================
  // BORDERS - 3 NÍVEIS DE INTENSIDADE (FASE 4.5)
  // ============================================================================
  
  /**
   * Border Colors
   * 3 níveis de intensidade para diferentes contextos
   */
  border: {
    divider: '#e2e8f0',    // gray[200] - Separadores sutis
    outline: '#cbd5e1',    // gray[300] - Bordas de inputs, cards
    strong: '#94a3b8',     // gray[400] - Bordas com ênfase
  },
};

// ============================================================================
// TIPOGRAFIA - HIERARQUIA CORPORATIVA
// ============================================================================

/**
 * Escala Tipográfica
 * Sistema modular baseado em 4px grid
 * 
 * @description FASE 4.5 - Typography Completo
 * Todas as variantes com font-size, line-height, weight e letter-spacing
 */
export const typography = {
  // Font Families
  fontFamily: {
    primary: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    mono: '"Roboto Mono", "Fira Code", Consolas, monospace',
  },

  // ============================================================================
  // DISPLAY & HEADINGS - Com line-height e weight completos
  // ============================================================================

  display: {
    fontSize: '3rem',           // 48px
    lineHeight: 1.2,            // 57.6px (tight)
    fontWeight: 700,            // bold
    letterSpacing: '-0.025em',  // tight
  },

  h1: {
    fontSize: '2rem',           // 32px - Main page headers
    lineHeight: 1.25,           // 40px (tight)
    fontWeight: 700,            // bold
    letterSpacing: '-0.025em',  // tight
  },

  h2: {
    fontSize: '1.5rem',         // 24px - Section headers
    lineHeight: 1.3,            // 31.2px
    fontWeight: 600,            // semibold
    letterSpacing: '-0.025em',  // tight
  },

  h3: {
    fontSize: '1.25rem',        // 20px - Subsection headers
    lineHeight: 1.4,            // 28px
    fontWeight: 600,            // semibold
    letterSpacing: '0',         // normal
  },

  h4: {
    fontSize: '1.125rem',       // 18px - Card titles
    lineHeight: 1.5,            // 27px (normal)
    fontWeight: 500,            // medium
    letterSpacing: '0',         // normal
  },

  h5: {
    fontSize: '1rem',           // 16px - Small titles
    lineHeight: 1.5,            // 24px (normal)
    fontWeight: 500,            // medium
    letterSpacing: '0',         // normal
  },

  h6: {
    fontSize: '0.875rem',       // 14px - Tiny titles
    lineHeight: 1.5,            // 21px (normal)
    fontWeight: 500,            // medium
    letterSpacing: '0.025em',   // wide
  },

  // ============================================================================
  // BODY TEXT - Conteúdo principal
  // ============================================================================

  body1: {
    fontSize: '1rem',           // 16px - Main content
    lineHeight: 1.5,            // 24px (normal)
    fontWeight: 400,            // regular
    letterSpacing: '0',         // normal
  },

  body2: {
    fontSize: '0.875rem',       // 14px - Secondary content
    lineHeight: 1.5,            // 21px (normal)
    fontWeight: 400,            // regular
    letterSpacing: '0',         // normal
  },

  // ============================================================================
  // SMALL TEXT - Captions, labels, auxiliares
  // ============================================================================

  caption: {
    fontSize: '0.75rem',        // 12px - Captions, labels
    lineHeight: 1.5,            // 18px (normal)
    fontWeight: 400,            // regular
    letterSpacing: '0.025em',   // wide
  },

  overline: {
    fontSize: '0.75rem',        // 12px - Overlines (uppercase labels)
    lineHeight: 1.5,            // 18px
    fontWeight: 500,            // medium
    letterSpacing: '0.1em',     // extra wide (for uppercase)
    textTransform: 'uppercase' as const,
  },

  // ============================================================================
  // FINANCIAL VALUES - Valores monetários (especial)
  // ============================================================================

  financialLarge: {
    fontSize: '2.25rem',        // 36px bold - Valor da Participação
    lineHeight: 1.2,            // 43.2px (tight)
    fontWeight: 700,            // bold
    letterSpacing: '-0.025em',  // tight
    fontFamily: '"Roboto Mono", "Fira Code", Consolas, monospace',
  },

  financialDefault: {
    fontSize: '1.5rem',         // 24px semibold - EBITDA, Receita
    lineHeight: 1.3,            // 31.2px
    fontWeight: 600,            // semibold
    letterSpacing: '0',         // normal
    fontFamily: '"Roboto Mono", "Fira Code", Consolas, monospace',
  },

  financialSmall: {
    fontSize: '1.125rem',       // 18px medium - Métricas secundárias
    lineHeight: 1.4,            // 25.2px
    fontWeight: 500,            // medium
    letterSpacing: '0',         // normal
    fontFamily: '"Roboto Mono", "Fira Code", Consolas, monospace',
  },

  // ============================================================================
  // BUTTON TEXT - Botões e CTAs
  // ============================================================================

  button: {
    fontSize: '0.875rem',       // 14px
    lineHeight: 1.5,            // 21px
    fontWeight: 500,            // medium
    letterSpacing: '0.025em',   // wide
    textTransform: 'none' as const,  // Sem uppercase forçado
  },

  buttonLarge: {
    fontSize: '1rem',           // 16px
    lineHeight: 1.5,            // 24px
    fontWeight: 500,            // medium
    letterSpacing: '0.025em',   // wide
    textTransform: 'none' as const,
  },

  // ============================================================================
  // LEGACY - Font Sizes, Weights, Line Heights (compatibilidade)
  // ============================================================================

  // Font Sizes (backward compatibility)
  fontSize: {
    display: '3rem',
    h1: '2rem',
    h2: '1.5rem',
    h3: '1.25rem',
    h4: '1.125rem',
    h5: '1rem',
    h6: '0.875rem',
    body1: '1rem',
    body2: '0.875rem',
    caption: '0.75rem',
    financialLarge: '2.25rem',
    financialDefault: '1.5rem',
    financialSmall: '1.125rem',
  },

  // Font Weights
  fontWeight: {
    light: 300,
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },

  // Line Heights
  lineHeight: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.75,
  },

  // Letter Spacing
  letterSpacing: {
    tighter: '-0.05em',
    tight: '-0.025em',
    normal: '0',
    wide: '0.025em',
    wider: '0.05em',
    widest: '0.1em',
  },
};

// ============================================================================
// ESPAÇAMENTOS - SISTEMA 4PX GRID
// ============================================================================

/**
 * Escala de Espaçamento
 * Baseada em múltiplos de 4px para consistência
 */
export const spacing = {
  xs: '0.25rem',    // 4px
  sm: '0.5rem',     // 8px
  md: '1rem',       // 16px
  lg: '1.5rem',     // 24px
  xl: '2rem',       // 32px
  '2xl': '3rem',    // 48px
  '3xl': '4rem',    // 64px
  '4xl': '6rem',    // 96px
};

// ============================================================================
// CARDS - REFINAMENTO CORPORATIVO
// ============================================================================

/**
 * Estilos de Card Corporativos
 */
export const card = {
  // Padding
  padding: {
    small: spacing.md,    // 16px
    medium: spacing.lg,   // 24px (novo padrão)
    large: spacing.xl,    // 32px
  },

  // Borders
  border: {
    width: '1px',         // Sutil (era 2px)
    color: colors.border.outline,  // Usando color token
    radius: '0.5rem',     // 8px
  },

  // ============================================================================
  // SHADOWS - LOW/MEDIUM/HIGH ELEVATION (FASE 4.5)
  // ============================================================================
  
  /**
   * Shadow System - 3 Níveis de Elevação
   * Baseado em Material Design elevation
   */
  shadow: {
    // Low Elevation (1-2dp) - Hover states, subtle depth
    low: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    
    // Medium Elevation (4-8dp) - Cards, dropdowns
    medium: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    
    // High Elevation (16-24dp) - Modals, popovers, floating elements
    high: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    
    // Legacy compatibility (mantém API antiga)
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },

  // Hover States
  hover: {
    borderColor: colors.brand[500],
    shadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    transform: 'translateY(-2px)',
    transition: 'all 0.2s ease-in-out',
  },
};

// ============================================================================
// BREAKPOINTS - RESPONSIVIDADE (FASE 4.5 - Documentação Expandida)
// ============================================================================

/**
 * Breakpoints para Media Queries
 * Alinhados com MUI defaults
 * 
 * @example
 * // Mobile-first approach
 * sx={{
 *   fontSize: '14px',           // mobile (xs)
 *   [theme.breakpoints.up('sm')]: { fontSize: '16px' },  // tablet
 *   [theme.breakpoints.up('md')]: { fontSize: '18px' },  // desktop
 * }}
 * 
 * @example
 * // Responsive values (MUI shorthand)
 * sx={{
 *   fontSize: { xs: '14px', sm: '16px', md: '18px' },
 *   padding: { xs: 2, md: 3, lg: 4 },
 * }}
 */
export const breakpoints = {
  xs: 0,      // Mobile portrait (0px+)
  sm: 600,    // Mobile landscape, small tablets (600px+)
  md: 960,    // Tablets, small laptops (960px+)
  lg: 1280,   // Desktops (1280px+)
  xl: 1920,   // Large desktops, 4K (1920px+)
};

// ============================================================================
// Z-INDEX - CAMADAS
// ============================================================================

/**
 * Z-Index Scale
 * Evita conflitos de camadas
 */
export const zIndex = {
  base: 0,
  dropdown: 1000,
  sticky: 1100,
  overlay: 1200,
  modal: 1300,
  popover: 1400,
  tooltip: 1500,
};

// ============================================================================
// ANIMAÇÕES - TRANSIÇÕES
// ============================================================================

/**
 * Timing Functions e Durações
 */
export const transitions = {
  duration: {
    fast: '150ms',
    normal: '200ms',
    slow: '300ms',
  },

  easing: {
    easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
    easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
    easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
  },
};

// ============================================================================
// ACESSIBILIDADE - WCAG AA
// ============================================================================

/**
 * Contrast Ratios (WCAG AA = 4.5:1 para texto normal)
 */
export const accessibility = {
  minContrast: 4.5,  // WCAG AA para texto normal
  minLargeContrast: 3,  // WCAG AA para texto grande (18px+)
  
  // Touch Targets (mobile)
  minTouchTarget: '44px',  // iOS/Android guidelines
  
  // Focus Indicators
  focusRing: {
    width: '2px',
    color: colors.brand[500],
    offset: '2px',
  },
};

// ============================================================================
// EXPORT DEFAULT - MUI THEME OVERRIDES (FASE 4.5 - Atualizado)
// ============================================================================

/**
 * MUI Theme Customization Object
 * Para usar em createTheme()
 * 
 * @example
 * import { createTheme } from '@mui/material/styles';
 * import { muiThemeOverrides } from './design-tokens';
 * 
 * const theme = createTheme(muiThemeOverrides);
 */
export const muiThemeOverrides = {
  palette: {
    primary: {
      main: colors.brand[500],
      light: colors.brand[300],
      dark: colors.brand[700],
    },
    secondary: {
      main: colors.info.main,
      light: colors.info.light,
      dark: colors.info.dark,
    },
    success: {
      main: colors.success.main,
      light: colors.success.light,
      dark: colors.success.dark,
    },
    warning: {
      main: colors.warning.main,
      light: colors.warning.light,
      dark: colors.warning.dark,
    },
    error: {
      main: colors.error.main,
      light: colors.error.light,
      dark: colors.error.dark,
    },
    info: {
      main: colors.info.main,
      light: colors.info.light,
      dark: colors.info.dark,
    },
    text: {
      primary: colors.text.primary,      // Usando text tokens (FASE 4.5)
      secondary: colors.text.secondary,
      disabled: colors.text.disabled,
    },
    background: {
      default: colors.bg.default,        // Usando bg tokens (FASE 4.5)
      paper: colors.bg.paper,
    },
    divider: colors.border.divider,      // Usando border tokens (FASE 4.5)
  },

  typography: {
    fontFamily: typography.fontFamily.primary,
    fontSize: 16,  // Base: 1rem = 16px
    
    // Usando typography completo (FASE 4.5)
    h1: {
      fontSize: typography.h1.fontSize,
      fontWeight: typography.h1.fontWeight,
      lineHeight: typography.h1.lineHeight,
      letterSpacing: typography.h1.letterSpacing,
    },
    h2: {
      fontSize: typography.h2.fontSize,
      fontWeight: typography.h2.fontWeight,
      lineHeight: typography.h2.lineHeight,
      letterSpacing: typography.h2.letterSpacing,
    },
    h3: {
      fontSize: typography.h3.fontSize,
      fontWeight: typography.h3.fontWeight,
      lineHeight: typography.h3.lineHeight,
      letterSpacing: typography.h3.letterSpacing,
    },
    h4: {
      fontSize: typography.h4.fontSize,
      fontWeight: typography.h4.fontWeight,
      lineHeight: typography.h4.lineHeight,
      letterSpacing: typography.h4.letterSpacing,
    },
    h5: {
      fontSize: typography.h5.fontSize,
      fontWeight: typography.h5.fontWeight,
      lineHeight: typography.h5.lineHeight,
      letterSpacing: typography.h5.letterSpacing,
    },
    h6: {
      fontSize: typography.h6.fontSize,
      fontWeight: typography.h6.fontWeight,
      lineHeight: typography.h6.lineHeight,
      letterSpacing: typography.h6.letterSpacing,
    },
    body1: {
      fontSize: typography.body1.fontSize,
      fontWeight: typography.body1.fontWeight,
      lineHeight: typography.body1.lineHeight,
      letterSpacing: typography.body1.letterSpacing,
    },
    body2: {
      fontSize: typography.body2.fontSize,
      fontWeight: typography.body2.fontWeight,
      lineHeight: typography.body2.lineHeight,
      letterSpacing: typography.body2.letterSpacing,
    },
    caption: {
      fontSize: typography.caption.fontSize,
      fontWeight: typography.caption.fontWeight,
      lineHeight: typography.caption.lineHeight,
      letterSpacing: typography.caption.letterSpacing,
    },
    overline: {
      fontSize: typography.overline.fontSize,
      fontWeight: typography.overline.fontWeight,
      lineHeight: typography.overline.lineHeight,
      letterSpacing: typography.overline.letterSpacing,
      textTransform: typography.overline.textTransform,
    },
    button: {
      fontSize: typography.button.fontSize,
      fontWeight: typography.button.fontWeight,
      lineHeight: typography.button.lineHeight,
      letterSpacing: typography.button.letterSpacing,
      textTransform: typography.button.textTransform,
    },
  },

  shape: {
    borderRadius: 8,  // 0.5rem
  },

  // Usando shadow system completo (FASE 4.5)
  shadows: [
    'none',
    card.shadow.low,      // elevation 1
    card.shadow.low,      // elevation 2
    card.shadow.medium,   // elevation 3
    card.shadow.medium,   // elevation 4
    card.shadow.medium,   // elevation 5
    card.shadow.medium,   // elevation 6
    card.shadow.medium,   // elevation 7
    card.shadow.high,     // elevation 8
    card.shadow.high,     // elevation 9
    card.shadow.high,     // elevation 10
    card.shadow.high,     // elevation 11
    card.shadow.high,     // elevation 12
    card.shadow.high,     // elevation 13
    card.shadow.high,     // elevation 14
    card.shadow.high,     // elevation 15
    card.shadow.high,     // elevation 16
    card.shadow.high,     // elevation 17
    card.shadow.high,     // elevation 18
    card.shadow.high,     // elevation 19
    card.shadow.high,     // elevation 20
    card.shadow.high,     // elevation 21
    card.shadow.high,     // elevation 22
    card.shadow.high,     // elevation 23
    card.shadow.high,     // elevation 24
  ],

  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: card.border.radius,
          border: `${card.border.width} solid ${card.border.color}`,
          boxShadow: card.shadow.medium,  // Usando shadow system
          transition: card.hover.transition,
          '&:hover': {
            borderColor: card.hover.borderColor,
            boxShadow: card.hover.shadow,
            transform: card.hover.transform,
          },
        },
      },
      defaultProps: {
        elevation: 0,  // Usar border ao invés de elevation
      },
    },

    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: card.padding.medium,
          '&:last-child': {
            paddingBottom: card.padding.medium,
          },
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: typography.button.textTransform,
          fontWeight: typography.button.fontWeight,
          borderRadius: card.border.radius,
          transition: transitions.duration.normal,
        },
        sizeLarge: {
          minHeight: accessibility.minTouchTarget,  // Touch target
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: typography.fontWeight.medium,
        },
      },
    },
  },
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Gera sombra customizada para elevação
 */
export const getElevation = (level: 0 | 1 | 2 | 3 | 4): string => {
  const shadows = ['none', card.shadow.sm, card.shadow.md, card.shadow.lg, card.shadow.xl];
  return shadows[level];
};

/**
 * Verifica contraste WCAG AA
 */
export const meetsWCAG_AA = (contrast: number): boolean => {
  return contrast >= accessibility.minContrast;
};

/**
 * Converte px para rem
 */
export const pxToRem = (px: number): string => {
  return `${px / 16}rem`;
};

/**
 * Gera transition CSS
 */
export const createTransition = (
  property: string,
  duration: keyof typeof transitions.duration = 'normal',
  easing: keyof typeof transitions.easing = 'easeInOut'
): string => {
  return `${property} ${transitions.duration[duration]} ${transitions.easing[easing]}`;
};

// ============================================================================
// MOBILE & TOUCH TARGETS - iOS/Android Guidelines
// ============================================================================

/**
 * Touch Targets Minimum Sizes
 * Based on iOS Human Interface Guidelines (44x44 pt) and Android Material Design (48x48 dp)
 * @see https://developer.apple.com/design/human-interface-guidelines/ios/visual-design/adaptivity-and-layout/
 * @see https://m3.material.io/foundations/accessible-design/accessibility-basics
 */
export const touchTargets = {
  // Minimum touch target sizes
  minimum: {
    width: '44px',      // iOS minimum
    height: '44px',     // iOS minimum
  },
  
  // Recommended touch target sizes (Android)
  recommended: {
    width: '48px',      // Android Material Design
    height: '48px',     // Android Material Design
  },

  // Icon buttons (small but still touch-friendly)
  iconButton: {
    minWidth: '44px',
    minHeight: '44px',
    padding: '10px',    // 44px - 24px icon = 20px padding (10px each side)
  },

  // Regular buttons
  button: {
    minHeight: '44px',
    paddingX: '16px',
    paddingY: '10px',
  },

  // Toggle buttons (wider for text labels)
  toggleButton: {
    minHeight: '44px',
    minWidth: '80px',   // Enough for short text labels
    paddingX: '20px',
    paddingY: '10px',
  },

  // Chips/Tags
  chip: {
    minHeight: '32px',  // Smaller OK (not primary action)
    paddingX: '12px',
    paddingY: '6px',
  },
};

/**
 * Mobile-friendly spacing between interactive elements
 */
export const mobileSpacing = {
  betweenButtons: '12px',   // Minimum 8px, recommended 12px
  betweenInputs: '16px',    // Comfortable spacing for forms
  sectionGap: '24px',       // Between major sections
};

/**
 * Chart responsive heights
 */
export const chartHeights = {
  mobile: '250px',    // Compact for mobile
  tablet: '350px',    // Medium for tablet
  desktop: '400px',   // Full for desktop
};

// ============================================================================
// USAGE EXAMPLES - FASE 4.5
// ============================================================================

/**
 * @example Usando Text Tokens
 * ```tsx
 * import { colors } from './design-tokens';
 * 
 * <Typography sx={{ color: colors.text.primary }}>
 *   Texto principal
 * </Typography>
 * 
 * <Typography sx={{ color: colors.text.secondary }}>
 *   Texto secundário
 * </Typography>
 * ```
 * 
 * @example Usando Background Tokens
 * ```tsx
 * <Box sx={{ 
 *   bgcolor: colors.bg.paper,
 *   border: `1px solid ${colors.border.outline}`,
 * }}>
 *   Card content
 * </Box>
 * ```
 * 
 * @example Usando Typography Completo
 * ```tsx
 * import { typography } from './design-tokens';
 * 
 * <Typography sx={{
 *   fontSize: typography.h2.fontSize,
 *   fontWeight: typography.h2.fontWeight,
 *   lineHeight: typography.h2.lineHeight,
 *   letterSpacing: typography.h2.letterSpacing,
 * }}>
 *   Section Header
 * </Typography>
 * ```
 * 
 * @example Usando Shadow System
 * ```tsx
 * import { card } from './design-tokens';
 * 
 * // Low elevation - subtle depth
 * <Card sx={{ boxShadow: card.shadow.low }}>...</Card>
 * 
 * // Medium elevation - standard cards
 * <Card sx={{ boxShadow: card.shadow.medium }}>...</Card>
 * 
 * // High elevation - modals, popovers
 * <Paper sx={{ boxShadow: card.shadow.high }}>...</Paper>
 * ```
 * 
 * @example Responsive Typography
 * ```tsx
 * import { typography } from './design-tokens';
 * 
 * <Typography sx={{
 *   fontSize: {
 *     xs: typography.body2.fontSize,   // 14px mobile
 *     md: typography.body1.fontSize,   // 16px desktop
 *   },
 *   lineHeight: {
 *     xs: typography.body2.lineHeight,
 *     md: typography.body1.lineHeight,
 *   },
 * }}>
 *   Responsive text
 * </Typography>
 * ```
 * 
 * @example Financial Values
 * ```tsx
 * import { typography } from './design-tokens';
 * 
 * <Typography sx={{
 *   fontSize: typography.financialLarge.fontSize,
 *   fontWeight: typography.financialLarge.fontWeight,
 *   fontFamily: typography.financialLarge.fontFamily,
 *   letterSpacing: typography.financialLarge.letterSpacing,
 * }}>
 *   R$ 8.574.750,00
 * </Typography>
 * ```
 * 
 * @example Border Tokens
 * ```tsx
 * import { colors } from './design-tokens';
 * 
 * // Subtle divider
 * <Divider sx={{ borderColor: colors.border.divider }} />
 * 
 * // Input outline
 * <TextField sx={{
 *   '& .MuiOutlinedInput-notchedOutline': {
 *     borderColor: colors.border.outline,
 *   }
 * }} />
 * 
 * // Strong emphasis border
 * <Box sx={{ border: `2px solid ${colors.border.strong}` }}>...</Box>
 * ```
 */
